import React, { useState } from "react";
import "../styles/cart.css";
import CommonSection from "../components/Ui/CommonSection";
import Helmet from "../components/Helmet/Helmet";
import { Container, Row, Col } from "react-bootstrap";
import { cartActions } from "../redux/slices/cartSlice";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmModal from "../components/Ui/ConfirmModal";
import RecentlyViewed from "../components/Ui/RecentlyViewed";
import { getProductImg, handleImgError } from "../utils/imageHelper";

function Cart() {
  const cartItems = useSelector((state) => state.cart.cartItems);
  const totalAmount = useSelector((state) => state.cart.totalAmount);
  const totalQuantity = useSelector((state) => state.cart.totalQuantity);
  const dispatch = useDispatch();

  const [itemToDelete, setItemToDelete] = useState(null);

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      dispatch(cartActions.removeItem(itemToDelete.id));
      toast.info(`Removed ${itemToDelete.productName} from your bag`);
      setItemToDelete(null);
    }
  };

  const handleIncreaseQty = (item) => {
    dispatch(
      cartActions.addItem({
        id: item.id,
        productName: item.productName,
        price: item.price,
        imgUrl: item.imgUrl,
      })
    );
  };

  const handleDecreaseQty = (item) => {
    if (item.quantity > 1) {
      dispatch(cartActions.deleteItem(item.id));
    } else {
      setItemToDelete(item);
    }
  };

  return (
    <Helmet title="Cart | L'HABILLEUR">
      <CommonSection title="Shopping Bag" />

      <section className="py-5">
        <Container>
          <Row className="g-4">
            <Col lg="8" md="12">
              {cartItems.length === 0 ? (
                <div className="cart__empty-state">
                  <div className="cart__empty-icon">
                    <i className="ri-shopping-cart-line"></i>
                  </div>
                  <h3>Your shopping bag is empty</h3>
                  <p>Explore our curated collections of luxury home furnishings and accessories.</p>
                  <Link to="/shop" className="buy__btn">
                    <i className="ri-store-2-line"></i> Explore Atelier Collection
                  </Link>
                </div>
              ) : (
                <div className="cart__table-card table-responsive">
                  <table className="cart__table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((item, index) => (
                        <Tr
                          item={item}
                          key={item.id || index}
                          onIncrease={() => handleIncreaseQty(item)}
                          onDecrease={() => handleDecreaseQty(item)}
                          onDeleteClick={() => setItemToDelete(item)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Col>

            <Col lg="4" md="12">
              <div className="cart__summary-card">
                <h5>Bag Summary</h5>

                <div className="cart__summary-row">
                  <span>Total Items</span>
                  <span className="fw-semibold text-dark">{totalQuantity} pcs</span>
                </div>

                <div className="cart__summary-row">
                  <span>Subtotal</span>
                  <span className="fw-semibold text-dark">${totalAmount}</span>
                </div>

                <div className="cart__summary-row">
                  <span>Estimated Delivery</span>
                  <span className="text-success fw-semibold">FREE</span>
                </div>

                <div className="cart__summary-row total">
                  <span>Estimated Total</span>
                  <span>${totalAmount}</span>
                </div>

                {/* VIP Privilege Voucher Teaser */}
                <div className="p-2 rounded mt-3 mb-2" style={{ background: 'rgba(212, 175, 55, 0.08)', border: '1px dashed rgba(212, 175, 55, 0.4)' }}>
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-coupon-3-line text-warning fs-5"></i>
                    <div>
                      <div className="small fw-bold text-dark">VIP Privilege Codes Available</div>
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        Apply <strong>ATELIER10</strong> or <strong>LUXURY50</strong> at checkout for bespoke savings.
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-muted small mt-2 mb-0">
                  Taxes, shipping addresses and payment confirmation are finalized securely at checkout.
                </p>

                {cartItems.length > 0 && (
                  <Link to="/checkout" className="cart__checkout-btn">
                    Proceed to Checkout <i className="ri-arrow-right-line ms-1"></i>
                  </Link>
                )}

                <Link to="/shop" className="cart__continue-btn">
                  Continue Shopping
                </Link>
              </div>
            </Col>
          </Row>

          {/* Recently Viewed Pieces to complement shopping bag */}
          <RecentlyViewed title="You Might Also Love (Recently Viewed)" />
        </Container>
      </section>

      {/* Remove Item Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(itemToDelete)}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove Piece from Bag?"
        badge="Bag Action"
        message={`Are you sure you want to remove "${itemToDelete?.productName}" from your shopping bag?`}
        confirmText="Remove Piece"
        cancelText="Keep in Bag"
        confirmVariant="danger"
        icon="ri-delete-bin-line"
      />
    </Helmet>
  );
}

const Tr = ({ item, onIncrease, onDecrease, onDeleteClick }) => {
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-3">
          <img
            src={getProductImg(item)}
            onError={(e) => handleImgError(e, item)}
            alt={item.productName}
            className="cart__item-img"
            width="65"
            height="65"
            loading="lazy"
            decoding="async"
          />
          <div>
            <div className="cart__item-title">{item.productName}</div>
            <span className="text-muted small">ID: #{item.id}</span>
          </div>
        </div>
      </td>
      <td>
        <span className="cart__item-price">${item.price}</span>
      </td>
      <td className="text-center">
        <div className="cart__qty-control">
          <button
            type="button"
            className="cart__qty-btn"
            onClick={onDecrease}
            title="Decrease quantity"
            aria-label="Decrease quantity"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="cart__qty-value">{item.quantity}</span>
          <button
            type="button"
            className="cart__qty-btn"
            onClick={onIncrease}
            title="Increase quantity"
            aria-label="Increase quantity"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>
      </td>
      <td className="text-end">
        <button
          type="button"
          onClick={onDeleteClick}
          className="cart__delete-btn"
          title="Remove piece from bag"
          aria-label="Remove item"
        >
          <i className="ri-delete-bin-line" />
        </button>
      </td>
    </tr>
  );
};

export default Cart;
